"use client";

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from './card';
import { Button } from './button';
import { Check, Bookmark, Building2, FolderOpen, Bot, Users, Infinity, Sparkles, Headphones, Shield, CheckCircle, Plug } from 'lucide-react';
import StatusBotIcon from '@/components/StatusBotIcon';
import { motion } from 'framer-motion';
import AnimatedText from '@/components/ui/animated-text';


const PricingCard = ({ title, description, price, pricingPeriod, items, cta, badge, icon, shadow, strikethroughPrice, showStrikethroughPrice = true, index, wrapperClass }) => (
  <Card className={`py-3 px-1 sm:py-4 md:py-6 space-y-0 sm:space-y-3 md:space-y-4 rounded-lg border bg-card text-card-foreground border-violet-500/20 shadow-sm ${wrapperClass}`}>
    <CardHeader className="px-3 py-0 sm:px-3 md:px-4 lg:px-6 flex-row justify-between space-y-0 items-center mb-0 sm:mb-2 md:mb-4 gap-2">
      <div className="flex-row items-center">{icon}</div>
      {badge && (
        <div className={`px-3 py-1 bg-violet-500/10 text-violet-500 rounded-full text-xs font-medium text-right w-fit ${
          index === 0 ? 'ring-2 ring-violet-500/20 shadow-lg shadow-violet-500/10' :
          index === 1 ? 'ring-1 ring-violet-500/10' :
          ''
        }`}>
          {badge}
        </div>
      )}
    </CardHeader>
    <CardContent className="px-3 py-0 sm:px-3 md:px-4 lg:px-6 pt-2">
      <CardTitle className="text-2xl font-bold">{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
      <div className="h-6 mb-1 mt-2 md:mt-3">
        {showStrikethroughPrice && strikethroughPrice && (
          <div className="text-muted-foreground line-through">{strikethroughPrice}</div>
        )}
      </div>
      <div className="flex items-end gap-2">
        <div className="text-3xl md:text-5xl font-bold">{price}</div>
        <div className="text-muted-foreground text-sm md:text-base">{pricingPeriod}</div>
      </div>
      <ul className="mt-4 space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-center gap-4">
            <div className="flex items-center justify-center w-6 md:w-10 h-6 md:h-10 rounded-full border border-muted-foreground/20 shrink-0">
              <div className="flex items-center justify-center w-full h-full ml-2">
                {item.icon}
              </div>
            </div>
            <div>
              <div className="text-sm font-normal md:text-sm md:font-semibold">{item.title}</div>
              <div className="text-xs md:text-sm text-muted-foreground">{item.description}</div>
            </div>
          </li>
        ))}
      </ul>
    </CardContent>
    <CardFooter className="px-3 py-0 sm:px-3 md:px-4 lg:px-6">
      {cta}
    </CardFooter>
  </Card>
);

const PricingLayout = () => {
  const [showMonthlyPricing, setShowMonthlyPricing] = useState(true);
  const navigate = useNavigate();

  return (
    <div className="container mx-auto py-12">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold tracking-wider whitespace-nowrap justify-self-center">
          <AnimatedText text="Simple, transparent pricing" />
        </h2>
        <p className="text-lg text-muted-foreground whitespace-nowrap justify-self-center">Choose a plan that fits your needs.</p>
        <div className="mt-6 flex justify-center">
          <div className="relative flex bg-violet-100/50 dark:bg-violet-900/30 rounded-full p-1">
            <div 
              className={`absolute top-1 bottom-1 bg-background dark:bg-violet-800 shadow-sm rounded-full transition-all duration-300 ease-in-out ${
                showMonthlyPricing 
                  ? 'left-1 right-1/2 mr-0.5' 
                  : 'left-1/2 right-1 ml-0.5'
              }`}
            />
            <button
              className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                showMonthlyPricing 
                  ? 'text-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setShowMonthlyPricing(true)}
            >
              Monthly
            </button>
            <button
              className={`relative z-10 px-6 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                !showMonthlyPricing 
                  ? 'text-foreground' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setShowMonthlyPricing(false)}
            >
              Annually
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
        <PricingCard
          title="Free"
          icon={<StatusBotIcon className="w-4 h-4 mr-2 text-violet-500" />}
          index={0}
          badge="Coming Soon"
          description="Perfect for personal projects"
          price="$0"
          pricingPeriod="/ user / month"
          // strikethroughPrice="$9"
          showStrikethroughPrice={showMonthlyPricing}
          items={[
            { title: 'Up to 3 projects', description: '', icon: <FolderOpen className="w-4 h-4 mr-2 text-violet-500" /> },
            { title: 'Basic AI features', description: '', icon: <Bot className="w-4 h-4 mr-2 text-violet-500" /> },
            { title: 'Community support', description: '', icon: <Users className="w-4 h-4 mr-2 text-violet-500" /> },
          ]}
          cta={<Button variant="secondary" className="w-full mt-4" onClick={() => navigate('/waitlist')}>Join Waitlist</Button>}
        />
        <PricingCard
          title="Pro"
          wrapperClass="bg-gradient-to-tl from-violet-600/30 to-background to-70%"
          icon={<Bookmark className="w-4 h-4 mr-2 text-violet-500" />}
          index={1}
          badge="Coming Soon"
          description="For growing teams"
          price={showMonthlyPricing ? "$19" : "$15"} // Monthly vs Annual pricing
          pricingPeriod="/ user / month"
          strikethroughPrice="$19"
          showStrikethroughPrice={!showMonthlyPricing}
          items={[
            { title: 'Unlimited projects', description: '', icon: <Infinity className="w-4 h-4 mr-2 text-violet-500" /> },
            { title: 'Advanced AI features', description: '', icon: <Sparkles className="w-4 h-4 mr-2 text-violet-500" /> },
            { title: 'Priority support', description: '', icon: <Headphones className="w-4 h-4 mr-2 text-violet-500" /> },
          ]}
          cta={<Button variant="default" className="w-full mt-4 bg-violet-600 hover:bg-violet-700 text-white" onClick={() => navigate('/waitlist')}>Join Waitlist</Button>}
        />
        <PricingCard
          title="Enterprise"
          icon={<Building2 className="w-4 h-4 mr-2 text-violet-500" />}
          index={2}
          badge="White Glove"
          description="For large organizations"
          price="Custom"
          pricingPeriod=""
          showStrikethroughPrice={false}
          items={[
            { title: 'Custom integrations', description: '', icon: <Plug className="w-4 h-4 mr-2 text-violet-500" /> },
            { title: 'Dedicated support', description: '', icon: <Shield className="w-4 h-4 mr-2 text-violet-500" /> },
            { title: 'SLA guarantees', description: '', icon: <CheckCircle className="w-4 h-4 mr-2 text-violet-500" /> },
          ]}
          cta={<Button variant="outline" className="w-full mt-4 border-violet-500 text-violet-600 hover:bg-violet-50 hover:text-violet-700" onClick={() => window.location.href = 'mailto:enterprise@status.bot'}>Contact Us</Button>}
        />
      </div>
    </div>
  );
};

export default PricingLayout;
